import type { AuditEventView } from '@blesk/contracts';
import { usePagedList } from '../../shared/lists';
import { useProjectOptions } from '../../shared/options';
import {
  AUDIT_ACTION_LABEL,
  auditActionLabel,
  auditFieldLabel,
  auditValueLabel,
} from '../../shared/labels';
import { formatInProjectTz } from '../../shared/format';
import { useSession } from '../../shared/session';
import { useUrlFilter } from '../../shared/filters';
import { PermissionGate } from '../shell/PermissionGate';
import { EmptyRow, ErrorNote, Pager, PeriodFilter, orDash } from '../shell/ui';

/** §13, §18 ТЗ, сценарий 33. Раздел «Аудит действий». */
export function AuditPage() {
  return (
    <PermissionGate permission="audit.read">
      <AuditInner />
    </PermissionGate>
  );
}

const ACTOR_LABEL: Record<AuditEventView['actorType'], string> = {
  EMPLOYEE: 'сотрудник',
  PERSON: 'администратор заказчика',
  SYSTEM: 'система',
};

function AuditInner() {
  const session = useSession();
  const [action, setAction] = useUrlFilter('action');
  const [projectId, setProjectId] = useUrlFilter('projectId');
  const [actorId, setActorId] = useUrlFilter('actorId');
  const [entityId, setEntityId] = useUrlFilter('entityId');
  const [from, setFrom] = useUrlFilter('from');
  const [to, setTo] = useUrlFilter('to');

  const projects = useProjectOptions();
  const list = usePagedList<AuditEventView>('audit', '/api/audit', {
    action,
    projectId,
    actorId,
    entityId,
    from,
    to,
  });

  /**
   * Список фильтра — только действующие объекты (§13), а событие могло
   * относиться к архивному. Показываем идентификатор: строка без объекта
   * читалась бы как внесистемное событие, которым она не является.
   */
  const projectName = (id: string | null): string => {
    if (!id) return '—';
    return projects.data?.find((project) => project.id === id)?.name ?? id;
  };

  // Имя автора берём из уже загруженной страницы: отдельного запроса «кто это»
  // ради подписи чипа не делаем, а по одному идентификатору отбор всё равно виден.
  const actorLabel = list.items.find((event) => event.actorId === actorId)?.actorName ?? actorId;

  return (
    <>
      <h1>Аудит действий</h1>
      <p className="muted">Записи неизменяемы; секретов, PIN и текстов сообщений в них нет.</p>

      <div className="toolbar">
        <label>
          Действие
          <select value={action} onChange={(event) => setAction(event.target.value)}>
            <option value="">Все</option>
            {Object.entries(AUDIT_ACTION_LABEL).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label>
          Объект
          <select value={projectId} onChange={(event) => setProjectId(event.target.value)}>
            <option value="">Все</option>
            {(projects.data ?? []).map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Идентификатор записи
          <input value={entityId} onChange={(event) => setEntityId(event.target.value)} />
        </label>
        <PeriodFilter from={from} to={to} onFrom={setFrom} onTo={setTo} />
      </div>

      {/* Отбор по автору и по записи выставляется кликом в таблице, поэтому он
          обязан быть виден и сниматься там же, где остальные фильтры. */}
      {(actorId || entityId) && (
        <div className="chips">
          {actorId && (
            <span className="chip">
              Автор: {actorLabel}
              <button
                type="button"
                aria-label="Снять отбор по автору"
                onClick={() => setActorId('')}
              >
                ✕
              </button>
            </span>
          )}
          {entityId && (
            <span className="chip">
              Запись: {entityId}
              <button
                type="button"
                aria-label="Снять отбор по записи"
                onClick={() => setEntityId('')}
              >
                ✕
              </button>
            </span>
          )}
        </div>
      )}

      <ErrorNote error={list.error} />

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              {/* Шесть колонок с текстом растягивали таблицу до 1828 px —
                  журнал уезжал вбок сильнее всех разделов (замер 18.08.2026).
                  Сведены пары, которые и читают вместе: когда и откуда,
                  кто и что сделал, объект и запись. */}
              <th>Когда</th>
              <th>Кто и что сделал</th>
              <th>Объект и запись</th>
              <th>Изменения</th>
            </tr>
          </thead>
          <tbody>
            {list.items.length === 0 && <EmptyRow colSpan={4} loading={list.isPending} />}
            {list.items.map((event) => (
              <tr key={event.id}>
                <td>
                  {formatInProjectTz(event.createdAt, session.defaultTimezone)}
                  {/* Адрес источника есть только у событий входа; у деловых событий
                      строка с прочерком без подписи читалась как потерянное значение
                      (QA 22.08.2026, дефект 10) */}
                  {event.ip && <div className="cell-note">{event.ip}</div>}
                </td>
                <td>
                  <Actor event={event} onPick={setActorId} />
                  <div className="cell-note">{auditActionLabel(event.action)}</div>
                </td>
                <td>
                  <div>{projectName(event.projectId)}</div>
                  {/* Сервер сверяет идентификатор целиком, а в ячейке видны
                      первые восемь символов: отбор ставим кликом, чтобы его
                      не пришлось выписывать вручную. */}
                  <button
                    type="button"
                    className="link-button"
                    title={`Отобрать события записи ${event.entityId}`}
                    onClick={() => setEntityId(event.entityId)}
                  >
                    {event.entityType}
                    <span className="muted"> · {event.entityId.slice(0, 8)}</span>
                  </button>
                </td>
                <td>
                  <Changes value={event.changes} timezone={session.defaultTimezone} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pager page={list.page} perPage={list.perPage} total={list.total} onPage={list.setPage} />
    </>
  );
}

/**
 * §18: у неудачного входа автора нет по определению — учётная запись
 * не подтверждена. Прочерк здесь читался бы как потерянное имя, а не как факт.
 */
function actorTitle(event: AuditEventView): string {
  if (event.actorName) return event.actorName;
  if (event.action === 'LOGIN_FAILED') return 'не аутентифицирован';
  return orDash(event.actorName);
}

function Actor({ event, onPick }: { event: AuditEventView; onPick: (actorId: string) => void }) {
  const actorId = event.actorId;
  return (
    <>
      {actorId ? (
        <button
          type="button"
          className="link-button"
          title="Отобрать события этого автора"
          onClick={() => onPick(actorId)}
        >
          {actorTitle(event)}
        </button>
      ) : (
        actorTitle(event)
      )}
      <span className="muted"> · {ACTOR_LABEL[event.actorType]}</span>
    </>
  );
}

/** Сколько пар изменений помещается в строку таблицы, не разгоняя её по высоте. */
// Две пары держат строку журнала в одну-две строки высотой: дальше таблица
// становится лестницей, и соседние события перестают попадать на экран разом.
const VISIBLE_FIELDS = 2;

/**
 * §18: состав изменений зависит от действия, но ключи повторяются от события
 * к событию, поэтому переводятся по словарю; неизвестный ключ остаётся как есть —
 * это не ошибка, а поле, которое ещё не описали.
 *
 * Ничего не обрезаем: остаток полей уезжает под «Остальные поля», иначе из
 * аудита пропадало бы именно то, ради чего в него смотрят.
 */
function Changes({ value, timezone }: { value: unknown; timezone: string }) {
  if (value === null || value === undefined) return <span className="muted">—</span>;
  if (typeof value !== 'object') return <>{auditValueLabel(value)}</>;

  const entries = Object.entries(value as Record<string, unknown>);
  if (entries.length === 0) return <span className="muted">—</span>;

  const hidden = entries.slice(VISIBLE_FIELDS);
  return (
    <>
      <ChangeList entries={entries.slice(0, VISIBLE_FIELDS)} timezone={timezone} />
      {hidden.length > 0 && (
        <details className="tech">
          <summary>Остальные поля ({hidden.length})</summary>
          <ChangeList entries={hidden} timezone={timezone} />
        </details>
      )}
    </>
  );
}

/**
 * Пары «поле — значение» в строку, а не столбиком: журнал просматривают
 * сверху вниз, и колонка по три строки на запись растягивает таблицу так,
 * что соседние события перестают попадать на экран разом.
 */
function ChangeList({
  entries,
  timezone,
}: {
  entries: Array<[string, unknown]>;
  timezone: string;
}) {
  return (
    <span className="changes">
      {entries.map(([key, item]) => (
        <span className="changes__item" key={key}>
          <span className="changes__key">{auditFieldLabel(key)}</span>
          <ChangeValue value={item} timezone={timezone} />
        </span>
      ))}
    </span>
  );
}

/** Момент времени в payload: `2026-08-18T21:54:00.000Z`. */
const ISO_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

/**
 * Ссылка на запись в payload: `{ id, name }` — так сервис пишет, например,
 * прежнего и нового менеджера объекта.
 */
function isNamedRef(value: object): value is { name: string; id?: unknown } {
  const keys = Object.keys(value);
  return (
    keys.every((key) => key === 'id' || key === 'name') &&
    typeof (value as { name?: unknown }).name === 'string' &&
    (value as { name: string }).name !== ''
  );
}

function ChangeValue({ value, timezone }: { value: unknown; timezone: string }) {
  if (value !== null && typeof value === 'object') {
    // «Менеджер изменён» — одно из главных событий журнала (§13), и прятать
    // готовое имя за раскрывашкой «структура» значит показать
    // «структура → структура» (QA 22.08.2026, дефект 9). Идентификатор
    // остаётся во всплывающей подсказке.
    if (isNamedRef(value)) {
      return <span title={typeof value.id === 'string' ? value.id : undefined}>{value.name}</span>;
    }
    // Вложенная структура (снимок правила, ответ портала) в ячейку не встанет,
    // но и терять её нельзя: прячем целиком, а не подрезаем до многоточия.
    return (
      <details className="tech">
        <summary>структура</summary>
        <pre className="snapshot">{JSON.stringify(value, null, 2)}</pre>
      </details>
    );
  }
  if (typeof value === 'string' && ISO_INSTANT.test(value)) {
    return <>{formatInProjectTz(value, timezone)}</>;
  }
  return <>{auditValueLabel(value)}</>;
}
